// "Ajustar y regenerar": traduce el pedido guardado de una imagen a la configuración que
// entiende la UI del modo (ADR-2026-09-09-ajustar-antes-de-regenerar).
//
// El backend devuelve el MISMO pedido que mandaría la regeneración a ciegas, en el
// vocabulario de `/v1/ads/generate` (`modelo_mode`, `background_mode`, `Format`…). Acá se
// invierte a lo que la pantalla usa (`ModelSelection`, `FondoSelection`, slug de formato),
// que es lo que viaja por el canal de restore de `InterfazV2`.
//
// Es la INVERSA de los builders de InterfazV2 (`buildDesignModeloBackground`,
// `guardadosFields`/`customFields`, `formatSlugToAspect`). Si alguno de ellos cambia, esto
// cambia con él: por eso vive en un módulo propio y con tests, en vez de repartido.
import type { FondoSelection } from '../FondoPicker';
import type { CustomModelFields, ModelSelection } from '../ModelPicker';

/**
 * Con qué pantalla se generó cada cosa → a qué modo hay que volver.
 *
 * La llave es `modo_utilizado`, que es lo que la pantalla escribe al generar y queda tanto
 * en la columna como en `datos_generacion`. Los `path` y los `presetId` son los de
 * `modesCatalog.ts`: si se renombra uno allá, hay que tocarlo acá (el test lo verifica
 * contra el catálogo real).
 *
 * `presetId` no viaja en la configuración —el modo lo abre la ruta— pero se queda en la
 * tabla porque es lo que prueba que cada `destino` apunta al modo que se cree.
 *
 * Los que NO están son deliberados:
 * - `chat_lote` (asistente): su configuración es una conversación con adjuntos; el propio
 *   catálogo ya dice que no se puede precargar por el canal de restore.
 * - `plantilla`: una plantilla no se abre por `/modo/:mode` sino con su propio preset, y
 *   mandar ahí un slug inventado deja al usuario en una pantalla muda. Queda pendiente.
 * - `catalogo` y `catalogo-muebles` (ADR-2026-09-13-catalogo-regenera-en-el-lugar): en el
 *   Catálogo lo que define la imagen es la VISTA (la card: frente, maniquí frontal…), y la
 *   vista no viaja por el canal de restore. Precargar producto, fondo y formato sin la
 *   card deja al usuario en el paso de elegir, o sea al principio del flujo: "manda para
 *   atrás". Ahí la acción correcta es regenerar EN EL LUGAR, que replica el mismo pedido
 *   —misma vista incluida— con la corrección del análisis adentro.
 * - los modos de video: no se analizan.
 */
export const MODOS_CONFIGURABLES: Record<string, { destino: string; presetId: string | null }> = {
  imagen_personalizada: { destino: 'modelo', presetId: 'imagen-con-modelo' },
  imagen_automatica: { destino: 'auto', presetId: 'imagen-auto-card' },
  fotos_producto: { destino: 'fotos-producto', presetId: 'fotos-producto-card' },
  anuncio: { destino: 'anuncio', presetId: 'anuncio-card' },
};

export const sePuedeConfigurar = (modoUtilizado: string | null | undefined): boolean =>
  !!modoUtilizado && modoUtilizado in MODOS_CONFIGURABLES;

/** Lo que el front necesita para dejar el modo listo. Todo opcional: se aplica lo que haya. */
export interface ConfiguracionDeRegeneracion {
  /**
   * `path` del modo en `modesCatalog` — la ruta a la que hay que ir. Navegar ahí es lo que
   * ABRE el modo, en el paso que le corresponde; la config sólo llena los campos
   * (ADR-2026-09-09-la-config-no-cambia-el-paso-del-panel).
   */
  destino: string;
  varianteIds: string[];
  /**
   * El modelo, ya en el vocabulario del picker. Para un modelo guardado viene SIN
   * `imageUrl` (el pedido no la lleva): la resuelve quien aplica, contra el catálogo de
   * modelos, igual que el restore resuelve los productos por id.
   */
  modelo: ModelSelection | null;
  /** Id del modelo guardado, para completar nombre e imagen contra el catálogo. */
  modeloGuardadoId: string | null;
  poseIds: string[];
  fondo: FondoSelection | null;
  /** Slug de formato (`post`, `story`, …); null si el pedido no traía uno reconocible. */
  formato: string | null;
  /** Lo que va en "indicaciones extras": lo del usuario + la corrección del análisis. */
  instruccion: string;
}

const texto = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

const lista = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string' && !!x);
  return typeof v === 'string' && v ? [v] : [];
};

/** Inversa de `formatSlugToAspect` de InterfazV2. */
const ASPECTO_A_SLUG: Record<string, string> = {
  '9:16': 'story',
  '1:1': 'square',
  '2:3': 'portrait',
  '16:9': 'banner',
  '3:2': 'editorial',
  '21:9': 'billboard',
  '4:5': 'post',
};

export function formatoDePedido(pedido: Record<string, unknown>): string | null {
  const aspecto = texto(pedido.Format) ?? texto(pedido.formato);
  return aspecto ? (ASPECTO_A_SLUG[aspecto] ?? null) : null;
}

/** Inversa de `buildDesignModeloBackground`. */
export function fondoDePedido(pedido: Record<string, unknown>): FondoSelection | null {
  const modo = texto(pedido.background_mode);
  if (!modo) return null;
  if (modo === 'ia') return { mode: 'auto' };
  if (modo === 'blanco') {
    const temperatura = texto(pedido.fondo_blanco_temperatura);
    const iluminacion = texto(pedido.fondo_blanco_iluminacion);
    return {
      mode: 'blanco',
      ...(temperatura ? { temperatura: temperatura as 'neutro' } : {}),
      ...(iluminacion === 'dura' || iluminacion === 'difusa' ? { iluminacion } : {}),
    };
  }
  if (modo === 'elegido') {
    const id = Number(pedido.background_id);
    // Sin nombre ni imagen en el pedido: los completa el picker cuando abre por id.
    return Number.isFinite(id) ? { mode: 'biblioteca', id, name: '', imageUrl: '' } : null;
  }
  if (modo === 'guardado') {
    const fileName = texto(pedido.background_file_name);
    return fileName ? { mode: 'guardado', fileName, name: '', imageUrl: '' } : null;
  }
  if (modo === 'descripcion') {
    const text = texto(pedido.background_text);
    return text ? { mode: 'descripcion', text } : null;
  }
  return null;
}

/** Inversa de `guardadosFields` / `customFields`. */
export function modeloDePedido(pedido: Record<string, unknown>): {
  modelo: ModelSelection | null;
  modeloGuardadoId: string | null;
} {
  const modo = texto(pedido.modelo_mode);
  if (modo === 'guardados') {
    const id = texto(pedido.modelo_guardado_id);
    if (!id) return { modelo: null, modeloGuardadoId: null };
    return {
      // `imageUrl` vacío a propósito: lo completa quien aplica, contra el catálogo.
      modelo: { mode: 'preset', id, name: texto(pedido.modelo_guardado_nombre) ?? '', imageUrl: '' },
      modeloGuardadoId: id,
    };
  }
  if (modo === 'personalizado' || modo === 'custom') {
    const fields: CustomModelFields = {};
    const genero = texto(pedido.genero);
    if (genero) fields.genero = genero;
    const edad = texto(pedido.edad);
    if (edad) fields.edad = edad;
    const tipoCuerpo = texto(pedido.tipo_cuerpo);
    if (tipoCuerpo) fields.tipoCuerpo = tipoCuerpo;
    const tonoPiel = texto(pedido.tono_piel);
    if (tonoPiel) fields.tonoPiel = tonoPiel;
    const colorPelo = texto(pedido.color_pelo);
    if (colorPelo) fields.colorPelo = colorPelo;
    const largoPelo = texto(pedido.largo_pelo);
    if (largoPelo) fields.largoPelo = largoPelo;
    const descripcion = texto(pedido.desc_modelo);
    if (descripcion) fields.descripcion = descripcion;
    return { modelo: { mode: 'custom', fields }, modeloGuardadoId: null };
  }
  if (modo === 'auto' || modo === 'ia') return { modelo: { mode: 'auto' }, modeloGuardadoId: null };
  return { modelo: null, modeloGuardadoId: null };
}

/**
 * El pedido guardado → la configuración del modo. `null` cuando el modo no se puede
 * precargar: ahí la opción no se ofrece y no hay a dónde ir.
 */
export function configuracionDeRegeneracion(
  pedido: Record<string, unknown>,
  modoUtilizado: string | null | undefined,
): ConfiguracionDeRegeneracion | null {
  if (!modoUtilizado) return null;
  const entrada = MODOS_CONFIGURABLES[modoUtilizado];
  if (!entrada) return null;
  const { modelo, modeloGuardadoId } = modeloDePedido(pedido);
  return {
    destino: entrada.destino,
    varianteIds: lista(pedido.variante_id),
    modelo,
    modeloGuardadoId,
    poseIds: lista(pedido.pose),
    fondo: fondoDePedido(pedido),
    formato: formatoDePedido(pedido),
    instruccion: texto(pedido.instruccion_adicional) ?? '',
  };
}
